import React from "react";
import { Box, Typography, IconButton, Collapse } from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { styles } from "@/config/project";

interface ExpandableWidgetProps {
    icon?: React.ReactNode;
    title?: string;
    children: React.ReactNode;
    attribution?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: string;
    expandable?: boolean;
    defaultExpanded?: boolean;
    noWrapper?: boolean;
    wrapUnbrokenText?: boolean;
}

export const ExpandableWidget: React.FC<ExpandableWidgetProps> = ({
    icon,
    title,
    children,
    attribution,
    accentColor = styles.warningDark,
    backgroundColor = styles.warningLight,
    textColor = styles.warningDark,
    borderRadius = "8px",
    expandable = true,
    defaultExpanded = false,
    noWrapper = false,
    wrapUnbrokenText = false,
}) => {
    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
    const overflowWrap = wrapUnbrokenText ? "anywhere" : undefined;

    const handleToggle = () => {
        if (expandable) {
            setIsExpanded(!isExpanded);
        }
    };

    const content = (
        <Box
            sx={{
                backgroundColor: accentColor,
                borderRadius: borderRadius,
                padding: "0",
                paddingLeft: "3px",
            }}
        >
            <Box
                sx={{
                    backgroundColor: backgroundColor,
                    borderRadius: "5px 8px 8px 5px",
                    padding: "12px",
                }}
            >
                {/* Header */}
                {title && (
                    <Box
                        onClick={handleToggle}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: expandable ? "pointer" : "default",
                            mb: expandable && isExpanded ? 1 : 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            {icon && (
                                <Box
                                    sx={{
                                        color: textColor,
                                        display: "flex",
                                        alignItems: "center",
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "0.75rem",
                                        },
                                    }}
                                >
                                    {icon}
                                </Box>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: textColor,
                                    overflowWrap,
                                }}
                            >
                                {title}
                            </Typography>
                        </Box>
                        {expandable && (
                            <IconButton
                                size="small"
                                sx={{
                                    color: textColor,
                                    padding: "2px",
                                    "&:hover": {
                                        backgroundColor: `${textColor}1A`, // 10% opacity
                                    },
                                    "& .MuiSvgIcon-root": {
                                        fontSize: "0.9rem",
                                    },
                                }}
                            >
                                {isExpanded ? <ExpandMore /> : <ChevronRight />}
                            </IconButton>
                        )}
                    </Box>
                )}

                {/* Content */}
                {expandable && title ? (
                    <Collapse in={isExpanded}>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: "0.75rem",
                                    color: textColor,
                                    mb: attribution ? 0.5 : 0,
                                    overflowWrap,
                                }}
                            >
                                {children}
                            </Typography>
                            {attribution && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: "0.7rem",
                                        color: (theme) =>
                                            theme.palette.text.secondary,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        overflowWrap,
                                    }}
                                >
                                    • {attribution}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                ) : (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: "0.75rem",
                                color: textColor,
                                mb: attribution ? 0.5 : 0,
                                overflowWrap,
                            }}
                        >
                            {children}
                        </Typography>
                        {attribution && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: "0.7rem",
                                    color: (theme) =>
                                        theme.palette.text.secondary,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    overflowWrap,
                                }}
                            >
                                • {attribution}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );

    if (noWrapper) {
        return content;
    }

    return (
        <Box
            sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                padding: "16px",
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            {content}
        </Box>
    );
};
